import {
  PRO_VERIFICATION_PENDING_LABEL,
  PRO_VERIFICATION_PENDING_SHEET_NOTE,
} from "@sill/shared/lib/verification-copy";
import { useState } from "react";
import { Link } from "react-router-dom";

import {
  formatDollars,
  formatPercent,
  offerSavingsLine,
  type OfferCardEstimatedSavings,
} from "@sill/shared/components/marketplace/offer-card";

import { ApiError } from "../../lib/api";
import { TARGET_CONFIRM_HREF } from "../../lib/target-confirm";
import { Dialog, DialogClose, DialogContent } from "../ui/dialog";
import { Button } from "../ui/button";
import type {
  BuyerConnectionDTO,
  ConnectionRequestDTO,
} from "../../lib/marketplace-status";

/**
 * AcceptConsentSheet — two-variant consent modal for marketplace offer acceptance
 * (M4 — plan 255, tasks 1123–1126).
 *
 * Variants:
 *   "active"   — `POST /api/marketplace/offers/:id/accept` (no body).
 *                 Returns `{ connection: BuyerConnectionDTO }`.
 *                 Copy: "Accept & connect" button.
 *   "standing" — `POST /api/agents/:proUserId/standing/accept`, body `{role, buyer_tier}`.
 *                 Returns `{ request: ConnectionRequestDTO }` for a stranger, or
 *                 `{ connection: BuyerConnectionDTO }` (already active) when the
 *                 buyer is the pro's own client — the sheet reads the reply's
 *                 shape and lands connected, never "Request sent", for the latter.
 *                 Copy: "Accept — ask [Agent] to confirm" button.
 *
 * Submit lifecycle (scenario 1133):
 *   idle → submitting → success-active  ("Connecting you…")
 *   idle → submitting → success-standing ("Request sent" + wired withdraw)
 *   idle → submitting → failure          (retryable)
 *
 * Load-bearing invariants:
 *   • EXACT copy strings exported as named constants — scenario 1131/1132 pins them.
 *   • No payment vocabulary anywhere buyer-facing.
 *   • Accept CTA chrome (trigger button) is a SIBLING to the shared OfferCard,
 *     never edited inside it — scenario 1135.
 *   • Handlers are injected props/fixtures in M4; M8 swaps to live `lib/marketplace-connection-api.ts`.
 *   • Fixture shapes mirror M6-B DTO contract exactly (field names are load-bearing
 *     for M8 shape-change-free swapover — decision 255).
 */

// ── Submit state machine ───────────────────────────────────────────────────────

/**
 * Every variant's `onAccept` resolves to one of these; the success phase is
 * chosen from the shape, not the variant, so an own client who reached the
 * standing sheet (the server decides own-client from ANY active link, the
 * page's own-agent signal only from referral links) still lands connected.
 */
export type AcceptOutcome =
  | { connection: BuyerConnectionDTO; request?: undefined }
  | { request: ConnectionRequestDTO; connection?: undefined };

export type AcceptSubmitPhase =
  | { phase: "idle" }
  | { phase: "submitting" }
  | { phase: "success-active"; connection?: BuyerConnectionDTO }
  | { phase: "success-standing"; requestId: string }
  | { phase: "failure"; reason?: "area_not_launched" | "agent_not_verified" | "buyer_off_market" };

import {
  ACTIVE_CONSENT_TEXT,
  ACTIVE_OTHER_OFFERS_NOTE,
  OWN_AGENT_FORMALIZATION_TEXT,
  OWN_AGENT_FORMALIZATION_TITLE,
  STANDING_OUTCOME_LINE,
} from "./AcceptConsentSheet.helpers";

// ── Props ─────────────────────────────────────────────────────────────────────

interface BaseConsentSheetProps {
  /** Whether the dialog is open. Managed by the caller (OfferDetailPage / AgentDetailPage). */
  open: boolean;
  /** Called when the dialog closes (escape, overlay click, or Done/Close button). */
  onClose: () => void;
  /** Agent's display name — drives standing outcome-line copy.
   *  Falls back to "your agent" / "The agent" when absent. */
  agentName?: string;
  /** Fired after a successful accept (either variant), before the buyer
   *  closes the success screen. Lets the caller refresh the live connection
   *  read so the status surface reflects the new charging connection. */
  onAccepted?: () => void;
  /** Additive M7 recovery disclosure. Omitted on the ordinary Plan255 path. */
  marketplaceRecoverySeverance?: boolean;
  /** SEC-07: the agent's license verification is submitted but not approved
   *  (`delivery_state === "pending_labeled"`). Renders the pending label on the
   *  marketplace variants; the own-agent sheet never shows it. */
  verificationPending?: boolean;
}

export interface ActiveConsentSheetProps extends BaseConsentSheetProps {
  variant: "active";
  /**
   * M4 fixture handler — caller provides a no-op or controlled fixture.
   * M8 wires: `() => acceptActiveOffer(getToken, offerId)`.
   * Response shape mirrors M6-B contract: `{ connection: BuyerConnectionDTO }`.
   * Field names are load-bearing for shape-change-free M8 swapover.
   */
  onAccept: () => Promise<{ connection: BuyerConnectionDTO }>;
}

/**
 * Direct-to-active acceptance for an agent the buyer is already linked to.
 * It intentionally shares the active accept action, but has its own light
 * consent treatment: no publish, reveal, fee, or severance framing.
 */
export interface OwnAgentConsentSheetProps extends BaseConsentSheetProps {
  variant: "own-agent";
  /** Dollar value from the offer's cash-credit lane, shown unchanged. */
  creditsAtClosing: number;
  /** Commission-rebate percentage from the offer's canonical terms summary. */
  commissionRebatePct?: number | null;
  /** Server-projected savings estimate already shown on the offer card. The
   * sheet words it with the card's own `offerSavingsLine`, so the buyer reads
   * one figure in one phrasing at the moment of consent. */
  estimatedSavings?: OfferCardEstimatedSavings | null;
  /** Success-state destinations. Kept callback-based so the sheet remains
   * reusable in previews and does not own application routing. */
  onMessageAgent?: () => void;
  onViewTeam?: () => void;
  onViewOffers?: () => void;
  onAccept: () => Promise<AcceptOutcome>;
}

export interface StandingConsentSheetProps extends BaseConsentSheetProps {
  variant: "standing";
  /**
   * M4 fixture handler — caller provides a no-op or controlled fixture.
   * M8 wires: `() => acceptStanding(getToken, proUserId, { role, buyer_tier })`.
   * Response shape mirrors M6-B contract: `{ request: ConnectionRequestDTO }`.
   * Field names are load-bearing — buyer_tier from the offer DTO (decision 227).
   * An own client's accept answers `{ connection }` instead (see AcceptOutcome).
   */
  onAccept: () => Promise<AcceptOutcome>;
  /**
   * Wired withdraw affordance (scenario 1133, standing success state).
   * M4 fixture: caller provides no-op. M8 wires:
   *   `(requestId) => withdrawRequest(getToken, requestId)`.
   * Called with the request id from the successful `onAccept` response.
   */
  onWithdraw?: (requestId: string) => Promise<void>;
}

export type AcceptConsentSheetProps =
  | ActiveConsentSheetProps
  | OwnAgentConsentSheetProps
  | StandingConsentSheetProps;

// ── AcceptConsentSheet ────────────────────────────────────────────────────────

export function AcceptConsentSheet(props: AcceptConsentSheetProps) {
  const {
    open,
    onClose,
    agentName,
    variant,
    onAccepted,
    marketplaceRecoverySeverance,
    verificationPending,
  } = props;
  const agentCopy = agentName ?? "your agent";
  const agentCapital = agentName ?? "The agent";
  const ownAgent = variant === "own-agent";
  // The card's own sentence ("About $X back on a $Y home/budget"): null for no
  // estimate or one that rounds to $0, so the tile then never renders.
  const ownAgentSavingsLine =
    props.variant === "own-agent" && props.estimatedSavings
      ? offerSavingsLine({ estimatedSavings: props.estimatedSavings })
      : null;

  const [submitState, setSubmitState] = useState<AcceptSubmitPhase>({
    phase: "idle",
  });

  const handleClose = () => {
    // Only reset state when not mid-flight (prevent close during submitting)
    if (submitState.phase !== "submitting") {
      setSubmitState({ phase: "idle" });
      onClose();
      // Once an own-agent offer is accepted, never reveal the stale detail
      // page underneath the dialog. Every dismissal lands on Your Offers,
      // whose fresh inbox/connection reads reflect the accepted agreement.
      if (
        ownAgent &&
        submitState.phase === "success-active" &&
        props.variant === "own-agent"
      ) {
        props.onViewOffers?.();
      }
    }
  };

  const handleAccept = async () => {
    setSubmitState({ phase: "submitting" });
    try {
      const result: AcceptOutcome | undefined = await props.onAccept();
      if (result?.request) {
        setSubmitState({
          phase: "success-standing",
          requestId: result.request.id,
        });
      } else {
        setSubmitState({
          phase: "success-active",
          connection: result?.connection,
        });
      }
      // Let the caller refresh its live connection read now that a charging
      // connection (or pending request) exists.
      onAccepted?.();
    } catch (error) {
      // Failure state: never surface raw error text or payment vocabulary.
      // The buyer sees a generic retryable message (scenario 1134), except
      // the server's launch-gate refusal (plan 454, 409 `area_not_launched`):
      // retrying cannot succeed, so it gets neutral copy and a way to change
      // the target instead of a "Try again".
      // SEC-07: 409 `agent_not_verified` is likewise not retryable until the
      // agent's license check completes. 409 `buyer_off_market` (a passport
      // that is not live; practically unreachable for a standing request now)
      // is never retryable either.
      setSubmitState(
        error instanceof ApiError && error.code === "area_not_launched"
          ? { phase: "failure", reason: "area_not_launched" }
          : error instanceof ApiError && error.code === "agent_not_verified"
            ? { phase: "failure", reason: "agent_not_verified" }
            : error instanceof ApiError && error.code === "buyer_off_market"
              ? { phase: "failure", reason: "buyer_off_market" }
              : { phase: "failure" },
      );
    }
  };

  // A standing accept the server bound instantly (an own client, already
  // active) renders the own-agent connected state — there is no "usually
  // takes a day" and nothing for the agent to confirm.
  const boundInstantly =
    variant === "standing" &&
    submitState.phase === "success-active" &&
    submitState.connection?.status === "active";
  const ownAgentSuccess =
    (ownAgent && submitState.phase === "success-active") || boundInstantly;
  const title = ownAgentSuccess
    ? "Great news 🎉"
    : ownAgent
      ? OWN_AGENT_FORMALIZATION_TITLE(agentCopy)
      : variant === "active"
        ? "Accept this offer"
        : "Request this offer";

  return (
    <Dialog
      onOpenChange={(isOpen) => {
        if (!isOpen) handleClose();
      }}
      open={open}
    >
      <DialogContent
        className="bg-popover"
        showClose={submitState.phase !== "submitting"}
        title={title}
      >
        <div data-testid="accept-consent-sheet-body">
          {/* ── SUCCESS: active ("Connecting you…") ── */}
          {submitState.phase === "success-active" && (
            <div className="space-y-4 py-2" data-testid="success-active">
              {ownAgentSuccess ? (
                <>
                  <div className="space-y-2 text-center">
                    <div
                      aria-hidden="true"
                      className="mx-auto flex size-14 items-center justify-center rounded-full bg-success/12 text-3xl ring-1 ring-success/20"
                    >
                      🎉
                    </div>
                    <p
                      className="text-wrap-balance text-xl font-bold tracking-tight text-foreground"
                      data-testid="success-headline"
                    >
                      You&rsquo;re all set with {agentCopy}!
                    </p>
                    <p className="text-wrap-pretty text-sm leading-6 text-muted-foreground">
                      Your offer is now part of your agreement. You can keep the
                      conversation going or review everything in Your Team.
                    </p>
                  </div>

                  <div
                    className="rounded-2xl bg-muted/50 px-4 py-3"
                    data-testid="own-agent-contact-card"
                  >
                    <p className="text-sm font-semibold text-foreground">
                      {agentCopy}
                    </p>
                    <p
                      className="mt-0.5 break-words text-sm text-muted-foreground"
                      data-testid="own-agent-contact"
                    >
                      {submitState.connection?.pro_contact ||
                        "Contact details are available in Your Team."}
                    </p>
                  </div>

                  <div className="space-y-2.5">
                    {props.variant === "own-agent" && props.onMessageAgent && (
                      <Button
                        className="w-full active:scale-[0.96] transition-transform"
                        data-testid="message-agent-success-cta"
                        onClick={props.onMessageAgent}
                      >
                        Message {agentCopy}
                      </Button>
                    )}
                    {props.variant === "own-agent" && props.onViewTeam && (
                      <Button
                        className="w-full active:scale-[0.96] transition-transform"
                        data-testid="view-team-success-cta"
                        onClick={props.onViewTeam}
                        variant="outline"
                      >
                        View your team
                      </Button>
                    )}
                    <button
                      className="min-h-10 w-full text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground"
                      data-testid="view-offers-success-cta"
                      onClick={
                        props.variant === "own-agent" && props.onViewOffers
                          ? props.onViewOffers
                          : handleClose
                      }
                      type="button"
                    >
                      Back to your offers
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p
                    className="text-base font-semibold text-foreground"
                    data-testid="success-headline"
                  >
                    Connecting you…
                  </p>
                  <p className="text-sm text-muted-foreground">
                    You&rsquo;re being connected with {agentCopy}. This usually
                    takes a day.
                  </p>
                  <Button
                    className="w-full"
                    onClick={handleClose}
                    variant="outline"
                  >
                    Done
                  </Button>
                </>
              )}
            </div>
          )}

          {/* ── SUCCESS: standing ("Request sent" + withdraw affordance) ── */}
          {submitState.phase === "success-standing" && (
            <div className="space-y-4 py-2" data-testid="success-standing">
              <p
                className="text-base font-semibold text-foreground"
                data-testid="success-headline"
              >
                Request sent
              </p>
              <p className="text-sm text-muted-foreground">
                {STANDING_OUTCOME_LINE(agentCapital)}
              </p>
              {/* Wired withdraw affordance (scenario 1133) */}
              {(props as StandingConsentSheetProps).onWithdraw && (
                <WithdrawAffordance
                  onWithdraw={(props as StandingConsentSheetProps).onWithdraw!}
                  requestId={submitState.requestId}
                />
              )}
              <Button
                className="w-full"
                onClick={handleClose}
                variant="outline"
              >
                Close
              </Button>
            </div>
          )}

          {/* ── FAILURE (retryable, no payment vocabulary — scenario 1134) ── */}
          {submitState.phase === "failure" &&
            submitState.reason === "area_not_launched" && (
              <div className="space-y-4 py-2" data-testid="area-not-launched-state">
                <p
                  className="text-sm text-muted-foreground"
                  data-testid="area-not-launched-message"
                >
                  Sill isn&rsquo;t live in your target area yet, so this
                  isn&rsquo;t available to you right now.
                </p>
                <Button asChild className="w-full">
                  <Link
                    data-testid="area-not-launched-change-target"
                    onClick={handleClose}
                    to={TARGET_CONFIRM_HREF}
                  >
                    Change my target area
                  </Link>
                </Button>
                <DialogClose asChild>
                  <Button className="w-full" variant="outline">
                    Close
                  </Button>
                </DialogClose>
              </div>
            )}
          {submitState.phase === "failure" &&
            submitState.reason === "agent_not_verified" && (
              <div className="space-y-4 py-2" data-testid="agent-not-verified-state">
                <p
                  className="text-sm text-muted-foreground"
                  data-testid="agent-not-verified-message"
                >
                  This offer can't be accepted until the agent's license
                  verification is complete.
                </p>
                <DialogClose asChild>
                  <Button className="w-full" variant="outline">
                    Close
                  </Button>
                </DialogClose>
              </div>
            )}
          {submitState.phase === "failure" &&
            submitState.reason === "buyer_off_market" && (
              <div className="space-y-4 py-2" data-testid="buyer-off-market-state">
                <p
                  className="text-sm text-muted-foreground"
                  data-testid="buyer-off-market-message"
                >
                  Your Buyer Passport isn&rsquo;t live, so this can&rsquo;t go
                  through right now.
                </p>
                <DialogClose asChild>
                  <Button className="w-full" variant="outline">
                    Close
                  </Button>
                </DialogClose>
              </div>
            )}
          {submitState.phase === "failure" && !submitState.reason && (
            <div className="space-y-4 py-2" data-testid="failure-state">
              <p
                className="text-sm text-muted-foreground"
                data-testid="failure-message"
              >
                Something went wrong — please try again.
              </p>
              <Button
                className="w-full"
                data-testid="failure-retry-button"
                onClick={() => setSubmitState({ phase: "idle" })}
              >
                Try again
              </Button>
              <DialogClose asChild>
                <Button className="w-full" variant="outline">
                  Cancel
                </Button>
              </DialogClose>
            </div>
          )}

          {/* ── IDLE / SUBMITTING ── */}
          {(submitState.phase === "idle" ||
            submitState.phase === "submitting") && (
            <div className="space-y-5 py-2">
              {ownAgent ? (
                <>
                  <p
                    className="text-wrap-pretty text-sm leading-6 text-foreground"
                    data-testid="own-agent-formalization-text"
                  >
                    {OWN_AGENT_FORMALIZATION_TEXT}
                  </p>
                  <div
                    className="grid gap-3 sm:grid-cols-2"
                    data-testid="own-agent-offer-terms"
                  >
                    <div
                      className="rounded-2xl border border-success/25 bg-success/12 px-4 py-3"
                      data-testid="own-agent-commission-rebate"
                    >
                      <p className="text-xs font-semibold text-muted-foreground">
                        Commission rebate
                      </p>
                      <p className="mt-1 tabular-nums text-lg font-bold tracking-tight text-success">
                        {props.variant === "own-agent" &&
                        props.commissionRebatePct != null
                          ? formatPercent(props.commissionRebatePct)
                          : "—"}
                      </p>
                    </div>
                    <div
                      className="rounded-2xl border border-success/25 bg-success/12 px-4 py-3"
                      data-testid="own-agent-credits"
                    >
                      <p className="text-xs font-semibold text-muted-foreground">
                        Credits at closing
                      </p>
                      <p className="mt-1 tabular-nums text-lg font-bold tracking-tight text-success">
                        {formatDollars(
                          props.variant === "own-agent"
                            ? props.creditsAtClosing
                            : 0,
                        )}
                      </p>
                    </div>
                  </div>
                  {ownAgentSavingsLine && (
                    <div
                      className="rounded-2xl bg-primary px-4 py-3 text-primary-foreground"
                      data-testid="own-agent-estimated-savings"
                    >
                      <p className="text-xs font-semibold text-primary-foreground/70">
                        Estimated savings
                      </p>
                      <p className="mt-1 tabular-nums text-xl font-bold tracking-tight">
                        {ownAgentSavingsLine}
                      </p>
                    </div>
                  )}
                </>
              ) : variant === "active" ? (
                /* Active: consent block (scenarios 1130/1131) */
                <>
                  <p
                    className="text-sm leading-6 text-foreground"
                    data-testid="active-consent-text"
                  >
                    {ACTIVE_CONSENT_TEXT(agentCopy)}
                  </p>
                  <p
                    className="text-xs text-muted-foreground"
                    data-testid="active-other-offers-note"
                  >
                    {ACTIVE_OTHER_OFFERS_NOTE}
                  </p>
                </>
              ) : null}

              {/* Standing: outcome line (scenario 1132) */}
              {variant === "standing" && (
                <p
                  className="text-sm leading-6 text-foreground"
                  data-testid="standing-outcome-line"
                >
                  {STANDING_OUTCOME_LINE(agentCapital)}
                </p>
              )}

              {verificationPending && !ownAgent && (
                <div
                  className="rounded-xl border border-warning/40 bg-warning/10 p-3 text-sm"
                  data-testid="accept-verification-pending"
                >
                  <p className="font-semibold text-warning">
                    {PRO_VERIFICATION_PENDING_LABEL}
                  </p>
                  <p className="mt-1 text-foreground">
                    {PRO_VERIFICATION_PENDING_SHEET_NOTE}
                  </p>
                </div>
              )}

              {marketplaceRecoverySeverance && !ownAgent && (
                <p
                  className="rounded-xl bg-muted p-3 text-sm"
                  data-testid="recovery-severance-copy"
                >
                  Your current agent will be notified only when this new
                  Marketplace connection activates. We never share the other
                  agent’s offer.
                </p>
              )}
              {/* Submit button — copy differs per variant */}
              <Button
                className="w-full"
                data-testid="accept-submit-button"
                disabled={submitState.phase === "submitting"}
                loading={submitState.phase === "submitting"}
                onClick={() => void handleAccept()}
              >
                {ownAgent
                  ? "Confirm"
                  : variant === "active"
                    ? "Accept & connect"
                    : `Accept — ask ${agentCopy} to confirm`}
              </Button>
              {ownAgent && (
                <Button
                  className="w-full"
                  onClick={handleClose}
                  variant="ghost"
                >
                  Not now
                </Button>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── WithdrawAffordance (standing success state) ───────────────────────────────

type WithdrawState =
  | { step: "idle" }
  | { step: "withdrawing" }
  | { step: "error" };

/**
 * Wired withdraw affordance rendered in the standing success state (scenario 1133).
 * M4: caller provides a fixture `onWithdraw` (no-op or controlled).
 * M8: wires `POST /api/marketplace/requests/:id/withdraw`.
 */
function WithdrawAffordance({
  requestId,
  onWithdraw,
}: {
  requestId: string;
  onWithdraw: (requestId: string) => Promise<void>;
}) {
  const [state, setState] = useState<WithdrawState>({ step: "idle" });

  const handleWithdraw = async () => {
    setState({ step: "withdrawing" });
    try {
      await onWithdraw(requestId);
      // Success: parent state would transition (e.g. close the sheet or update
      // the connection status). The sheet sheet stays open; parent controls next step.
      setState({ step: "idle" });
    } catch {
      setState({ step: "error" });
    }
  };

  return (
    <div data-testid="withdraw-affordance">
      {state.step === "error" && (
        <p
          className="mb-2 text-xs text-destructive"
          data-testid="withdraw-error"
        >
          Couldn't withdraw — please try again.
        </p>
      )}
      <button
        className="text-sm font-semibold text-muted-foreground underline-offset-2 hover:text-foreground hover:underline disabled:cursor-not-allowed disabled:opacity-50"
        data-testid="withdraw-trigger"
        disabled={state.step === "withdrawing"}
        onClick={() => void handleWithdraw()}
        type="button"
      >
        {state.step === "withdrawing" ? "Withdrawing…" : "Withdraw request"}
      </button>
    </div>
  );
}
